import React from "react";
import { Colors, Validator } from "@utils";
import styled from "styled-components";
import { Button, Input } from "@components/atoms";
import { useAppDispatch } from "@store";
import { setUserInfo } from "@store/slices/userInfoSlice";
import { useNavigate } from "react-router-dom";
import { AuthTypeButton } from "@components/organisms";
import { NationalDataAuthType } from "@utils/Constants";
import { useUserInfo } from "@store/hooks";
import { clearAuthInfo } from "@store/slices/dataSlice";

const AuthTypeGrid = styled.div`
  align-self: center;
  display: grid;
  width: fit-content;
  grid-template-columns: repeat(2, auto);
  column-gap: 20px;
  row-gap: 10px;
`;

const InputTitle = styled.span`
  font-size: 14px;
  font-weight: 400;
  color: ${Colors.darkGray};
  margin-bottom: 10px;
`;

const AUTH_TYPES = [
  NationalDataAuthType.Kakao,
  NationalDataAuthType.Naver,
  NationalDataAuthType.Pass,
];

function InputInfoPage() {
  const userInfo = useUserInfo();

  const [authType, setAuthType] = React.useState<
    Optional<NationalDataAuthType>
  >(userInfo.authType);
  const [name, setName] = React.useState<string>(userInfo.name ?? "");
  const [phone, setPhone] = React.useState<string>(userInfo.phone ?? "");
  const [birthday, setBirthday] = React.useState<string>(
    userInfo.birthday ?? ""
  );

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const onSubmit = () => {
    dispatch(clearAuthInfo());
    dispatch(
      setUserInfo({
        authType: authType!,
        name: name.trim(),
        phone,
        birthday,
      })
    );

    navigate(`/auth_tutorial`);
  };

  const valid = React.useMemo(() => {
    return (
      authType &&
      name.trim().length > 0 &&
      Validator.isBirthday(birthday) &&
      Validator.isPhoneNumber(phone)
    );
  }, [authType, birthday, name, phone]);

  return (
    <div className="flex flex-col p-[22px] min-h-full">
      <span
        className="text-[15px]/[26px] font-[700] mb-[7px]"
        style={{ color: Colors.totallyBlack }}
      >
        인증서 선택
      </span>
      <AuthTypeGrid>
        {AUTH_TYPES.map((authTypeItem) => (
          <AuthTypeButton
            key={authTypeItem}
            authType={authTypeItem}
            onSelectType={(selectedAuthType) => setAuthType(selectedAuthType)}
            selected={authTypeItem === authType}
          />
        ))}
      </AuthTypeGrid>
      <span
        className="text-[15px]/[26px] font-[700] mt-[24px] mb-[20px]"
        style={{ color: Colors.totallyBlack }}
      >
        개인정보 입력
      </span>
      <InputTitle>이름</InputTitle>
      <Input
        style={{ marginBottom: 12 }}
        placeholder="이름을 입력하세요."
        maxLength={10}
        value={name}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          setName(e.target.value)
        }
      />
      <InputTitle>생년월일</InputTitle>
      <Input
        style={{ marginBottom: 12 }}
        mask="9999.99.99"
        maskPlaceholder=""
        placeholder="8자리로 입력해주세요. ex) 1999.11.11"
        value={birthday}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          setBirthday(e.target.value)
        }
        inputMode="numeric"
      />
      <InputTitle>전화번호</InputTitle>
      <Input
        mask="999-9999-9999"
        maskPlaceholder=""
        placeholder="000-0000-0000"
        value={phone}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          setPhone(e.target.value)
        }
        inputMode="numeric"
      />
      <div className="grow" />
      <div
        className="text-[13px]/[15px] font-[600] text-center mt-[20px] whitespace-pre-wrap text-center"
        style={{ color: Colors.gray1 }}
      >
        건강성적표는 개인정보보호법을 준수하며{"\n"}
        입력하신 개인정보는 서비스 외 다른 용도로 사용되지 않습니다.
      </div>
      <Button
        disabled={!valid}
        className="mt-[11px]"
        bgColor={Colors.primaryGreen}
        color={Colors.white}
        onClick={onSubmit}
      >
        확인
      </Button>
    </div>
  );
}

export default InputInfoPage;
